import { Box, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import { FC, useMemo, useState } from 'react';
import { C, FONT, int, monthLabel } from './ui';
import type { DeliveryDay, ListGrowthMonth, ListRow, ListSizeDay } from './types';

const nivoTheme = {
  background: 'transparent',
  text: { fontSize: 12, fill: C.ink2, fontFamily: FONT },
  axis: {
    domain: { line: { stroke: 'transparent' } },
    ticks: { line: { stroke: 'transparent' }, text: { fontSize: 11, fill: C.ink3 } },
  },
  grid: { line: { stroke: '#eef1f5', strokeWidth: 1 } },
  crosshair: { line: { stroke: C.ink3, strokeWidth: 1, strokeOpacity: 0.6 } },
};

const LIST_COLORS = ['#0b4f8a', '#12a150', '#f08c00', '#8b5cf6', '#e5484d', '#0e9aa7', '#c2410c', '#5aa9e6'];

const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const shortMonth = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return new Intl.DateTimeFormat('de-DE', { month: 'short', year: '2-digit' }).format(new Date(Date.UTC(y, m - 1, 15)));
};

const Tip: FC<{ title: string; rows: Array<{ color: string; label: string; value: string }> }> = ({ title, rows }) => (
  <Box sx={{ bgcolor: '#fff', border: `1px solid ${C.line}`, borderRadius: '10px', px: 1.5, py: 1, boxShadow: '0 4px 14px rgba(16,24,40,0.12)', fontSize: 13, color: C.ink }}>
    <Box sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Box>
    {rows.map((r) => (
      <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: r.color }} />
        <span>{r.label}</span>
        <b style={{ marginLeft: 'auto', paddingLeft: 12 }}>{r.value}</b>
      </Box>
    ))}
  </Box>
);

const Legend: FC<{ items: Array<{ label: string; color: string }> }> = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', mt: 1.5 }}>
    {items.map((i) => (
      <Box key={i.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13, color: C.ink2 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: i.color }} />
        {i.label}
      </Box>
    ))}
  </Box>
);

/** Gelieferte Leads je Berliner Tag, Prio 1 und Prio 2 gestapelt. Luecken werden mit 0 gefuellt. */
export const DeliveriesChart: FC<{ days: DeliveryDay[] }> = ({ days }) => {
  const data = useMemo(() => {
    if (days.length === 0) return [];
    const byDay = new Map(days.map((d) => [d.day, d]));
    const out: Array<{ day: string; prio1: number; prio2: number }> = [];
    const cur = new Date(`${days[0].day}T12:00:00Z`);
    const end = new Date(`${days[days.length - 1].day}T12:00:00Z`);
    while (cur <= end) {
      const k = cur.toISOString().slice(0, 10);
      const d = byDay.get(k);
      out.push({ day: k, prio1: d?.prio1 ?? 0, prio2: d?.prio2 ?? 0 });
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return out.slice(-60);
  }, [days]);
  const step = Math.max(1, Math.ceil(data.length / 10));
  if (data.length === 0) return <Typography sx={{ color: C.ink2, fontSize: 14 }}>Noch keine Lieferungen.</Typography>;
  return (
    <Box>
      <Box sx={{ height: 260 }}>
        <ResponsiveBar
          data={data}
          keys={['prio1', 'prio2']}
          indexBy="day"
          margin={{ top: 8, right: 8, bottom: 28, left: 44 }}
          padding={0.25}
          colors={({ id }) => (id === 'prio1' ? C.prio1 : C.prio2)}
          theme={nivoTheme}
          enableLabel={false}
          animate={false}
          borderRadius={2}
          axisLeft={{ tickSize: 0, tickPadding: 8, format: (v) => int(Number(v)) }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            format: (v) => ddmm(String(v)),
            tickValues: data.filter((_, i) => i % step === 0).map((d) => d.day),
          }}
          tooltip={({ indexValue, data: d }) => (
            <Tip
              title={ddmm(String(indexValue))}
              rows={[
                { color: C.prio1, label: 'Prio 1', value: int(Number(d.prio1)) },
                { color: C.prio2, label: 'Prio 2', value: int(Number(d.prio2)) },
              ]}
            />
          )}
        />
      </Box>
      <Legend items={[{ label: 'Prio 1', color: C.prio1 }, { label: 'Prio 2', color: C.prio2 }]} />
    </Box>
  );
};

const PRESETS = [14, 30, 90, 180] as const;
const minusDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};

/**
 * Angemeldete Kontakte je Liste: taeglich mit Zeitraumwahl (Vorgabe 14 Tage) oder monatlich (Mailchimp-Historie).
 * Tageswerte vor Beginn der Messung sind aus Mailchimps Tagesaktivitaet zurueckgerechnet (`estimated`).
 */
export const ListSizeChart: FC<{ monthly: ListGrowthMonth[]; daily: ListSizeDay[]; lists: ListRow[]; names: Record<string, string> }> = ({
  monthly,
  daily,
  lists,
  names,
}) => {
  const [mode, setMode] = useState<'daily' | 'monthly'>('daily');
  const lastDay = useMemo(() => daily.reduce((m, d) => (d.day > m ? d.day : m), ''), [daily]);
  const firstDay = useMemo(() => daily.reduce((m, d) => (!m || d.day < m ? d.day : m), ''), [daily]);
  const [preset, setPreset] = useState<number | null>(14);
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const range = useMemo(() => {
    const to = bis || lastDay;
    const from = preset ? minusDays(to, preset - 1) : von || firstDay;
    return { from, to };
  }, [preset, von, bis, lastDay, firstDay]);

  const accounts = useMemo(() => {
    const s = new Set<string>([...lists.map((l) => l.account), ...monthly.map((m) => m.account)]);
    return [...s].sort((a, b) => (names[a] ?? a).localeCompare(names[b] ?? b));
  }, [lists, monthly, names]);
  const imZeitraum = useMemo(
    () => daily.filter((d) => d.day >= range.from && d.day <= range.to),
    [daily, range],
  );
  const series = useMemo(
    () =>
      accounts.map((a, i) => ({
        id: names[a] ?? a,
        color: LIST_COLORS[i % LIST_COLORS.length],
        data:
          mode === 'monthly'
            ? monthly.filter((m) => m.account === a && m.subscribed !== null).map((m) => ({ x: m.month, y: m.subscribed as number }))
            : imZeitraum.filter((d) => d.account === a).map((d) => ({ x: d.day, y: d.memberCount })),
      })),
    [accounts, mode, monthly, imZeitraum, names],
  );
  const xs = [...new Set(series.flatMap((s) => s.data.map((p) => p.x)))].sort();
  const step = Math.max(1, Math.ceil(xs.length / 8));
  const fmtX = (v: string) => (mode === 'monthly' ? shortMonth(v) : ddmm(v));
  const leer = series.every((s) => s.data.length === 0);
  const ersteMessung = useMemo(() => daily.filter((d) => !d.estimated).reduce((m, d) => (!m || d.day < m ? d.day : m), ''), [daily]);
  const geschaetztImBild = mode === 'daily' && imZeitraum.some((d) => d.estimated);
  const dateField = { size: 'small' as const, type: 'date', InputLabelProps: { shrink: true }, inputProps: { min: firstDay, max: lastDay }, sx: { width: 150 } };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        {mode === 'daily' ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={preset}
              onChange={(_, v) => {
                if (v === null) return;
                setPreset(v);
                setVon('');
                setBis('');
              }}
            >
              {PRESETS.map((p) => (
                <ToggleButton key={p} value={p} sx={{ textTransform: 'none', px: 1.25 }}>
                  {p} Tage
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              {...dateField}
              label="Von"
              value={range.from}
              onChange={(e) => {
                setPreset(null);
                setVon(e.target.value);
                if (!bis) setBis(range.to);
              }}
            />
            <TextField
              {...dateField}
              label="Bis"
              value={range.to}
              onChange={(e) => {
                if (preset) setVon(range.from);
                setPreset(null);
                setBis(e.target.value);
              }}
            />
          </Box>
        ) : (
          <Box />
        )}
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          <ToggleButton value="daily" sx={{ textTransform: 'none', px: 1.5 }}>Täglich</ToggleButton>
          <ToggleButton value="monthly" sx={{ textTransform: 'none', px: 1.5 }}>Monatlich (24 Monate)</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {leer ? (
        <Typography sx={{ color: C.ink2, fontSize: 14, py: 4 }}>Keine Werte in diesem Zeitraum.</Typography>
      ) : (
        <Box sx={{ height: 300 }}>
          <ResponsiveLine
            data={series}
            margin={{ top: 10, right: 12, bottom: 30, left: 56 }}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
            curve="monotoneX"
            colors={(d) => d.color as string}
            theme={nivoTheme}
            enablePoints={xs.length <= 31}
            pointSize={5}
            lineWidth={2}
            animate={false}
            useMesh
            enableSlices="x"
            axisLeft={{ tickSize: 0, tickPadding: 8, format: (v) => int(Number(v)) }}
            axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: xs.filter((_, i) => i % step === 0), format: (v) => fmtX(String(v)) }}
            sliceTooltip={({ slice }) => (
              <Tip
                title={mode === 'monthly' ? monthLabel(String(slice.points[0].data.x)) : ddmm(String(slice.points[0].data.x))}
                rows={[...slice.points]
                  .sort((a, b) => Number(b.data.y) - Number(a.data.y))
                  .map((p) => ({ color: p.serieColor, label: String(p.serieId), value: int(Number(p.data.y)) }))}
              />
            )}
          />
        </Box>
      )}
      <Legend items={series.map((s) => ({ label: String(s.id), color: s.color }))} />
      {geschaetztImBild && (
        <Typography sx={{ fontSize: 12, color: C.ink3, mt: 1 }}>
          Tageswerte vor dem {ersteMessung ? ddmm(ersteMessung) : 'Messbeginn'} sind aus Mailchimps Tagesaktivität zurückgerechnet
          (Tagesende; gegen Mailchimps Monatswerte geprüft, Abweichung unter 0,3 %).
        </Typography>
      )}
    </Box>
  );
};
